import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import {
  PlaceOfResidence,
  SignUp,
  placeOfResidenceSchema,
  weaponSelectionSchema,
  WeaponSelection,
} from '@/features/ui';
import { AdditionalDocuments } from '@/features/ui/Steps/AdditionalDocuments';
import { additionalDocumentsSchema } from '@/features/ui/Steps/AdditionalDocuments/model/schema';
import { PersonalAndPassportData, personalAndPassportDataSchema } from '@/features/ui/Steps/PersonalAndPassportData';
import { TechnicalConditionData } from '@/features/ui/Steps/TechnicalConditionData';
import { technicalConditionDataSchema } from '@/features/ui/Steps/TechnicalConditionData/model/schema';

import { useFormDraftPlainTools } from '@/hooks/index';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserRequestDraft, UserService } from '@/api/types';

import api from '@/api';

import {
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  SERVICE_CODE,
  STEP_NUMBER_SIGN_UP,
} from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { ServiceProps } from './model/types';
import { CommissionSalesResult } from './ui/CommissionSalesResult';
import { DataPreview } from './ui/DataPreview';

import './CommissionSales.scss';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Вибір зброї',
    step: WeaponSelection,
    validationSchema: weaponSelectionSchema,
  },
  {
    title: 'Особисті та паспортні дані',
    step: PersonalAndPassportData,
    validationSchema: personalAndPassportDataSchema,
  },
  {
    title: 'Місце зберігання зброї',
    step: PlaceOfResidence,
    validationSchema: placeOfResidenceSchema,
  },
  {
    title: 'Технічний стан та відстріл',
    step: TechnicalConditionData,
    validationSchema: technicalConditionDataSchema,
  },
  {
    title: 'Додаткові дані',
    step: AdditionalDocuments,
    validationSchema: additionalDocumentsSchema,
  },
  {
    title: 'Підтвердження даних',
    step: DataPreview,
  },
  {
    title: 'Підписання документу КЕП',
    step: SignUp,
  },
  {
    title: 'Результат',
    step: CommissionSalesResult,
  },
];

export const CommissionSales: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const currentService = getServiceByCode(SERVICE_CODE) as UserService;
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const {
    isLoading: isLoadingDraft,
    onDraftContinue,
    onDraftSave,
    onDraftCreate,
    currentRequestId,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => {
      setDefaultValues(data ?? {});
    },
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
  });

  const { mutateAsync: generatePDFMutation, isPending: isLoadingPdf } = useMutation({
    mutationFn: async ({
      code,
      payload,
    }: {
      code: Parameters<typeof api.services.generatePdf>[0];
      payload: Parameters<typeof api.services.generatePdf>[1];
    }) => {
      const result = await api.services.generatePdf(code, payload);
      serviceFileManager.setAttachment(result);
      return result;
    },
  });

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });

  const handleDraftCreate = async (_: any, specificStep?: (step: number) => void): Promise<Nullable<string>> =>
    specificStep &&
    onDraftCreate(
      {
        id: currentService.id,
        name: currentService.name,
        code: SERVICE_CODE,
        totalSteps: STEPS.length,
      },
      specificStep,
    );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
      });
      await generatePDFMutation({ code: SERVICE_CODE, payload: commonPayload });
    },
    [currentRequestId, currentService, generatePDFMutation],
  );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      });

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm],
  );

  const isLoading = isLoadingDraft || isLoadingPdf || isLoadingSubmit;

  return (
    <ServiceFormTemplate<ServiceProps>
      className="commision-sales"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      skipAvailabilityValidation={(step) => step === 2}
      currentRequestId={currentRequestId ?? null}
      onDraftCreate={handleDraftCreate}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      stepProps={{
        handleGeneratePDF,
        serviceFileManager,
        submitForm: handleSubmitForm,
        showNpuPaymentInformer: false,
        disallowedDocumentTypeCodes: disallowedDocumentTypeCodesForAdditionalDocumentStep,
      }}
    />
  );
};
